/* eslint-disable @next/next/no-img-element */
/* eslint-disable no-fallthrough */
/* eslint-disable no-case-declarations */
import RoundedButton from 'components/RoundedButton';
import { useEffect, useRef, useState } from 'react';
import { Message } from 'semantic-ui-react';
// @ts-ignore
import BackButton from 'components/BackButton';
import { EntryMethod } from 'components/enter/EntryMethod';
import GenericModal from 'components/GenericModal';
import AdyenDropInCheckout from 'components/payments/AdyenDropInCheckout';
import PlainLoadingSpinner from 'components/PlainLoadingSpinner';
import Subheading from 'components/Subheading';
import { useDrawStore } from 'hooks/stores/useDrawStore';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import {
  fetchUtmParamsJoinedWithFbDataFromLocalStorage,
  trackEecCheckoutOption,
} from 'utils/analytics/analytics';
import { scrollToSection, sleep } from 'utils/common';
import { ASSET_PREFIX } from 'utils/constants';
import { SOURCE_QUERY_PARAM } from '../../pages/validate-3ds-payment';

export const PromoCodeRewards = {
  FREE_ENTRY: 'FREE_ENTRY',
  DISCOUNT_PERCENTAGE: 'DISCOUNT_PERCENTAGE',
  DISCOUNT_ENTRY: 'DISCOUNT_ENTRY',
  FREE_GOODIEBOX: 'FREE_GOODIEBOX',
  FREE_TRAVEL_RAFFLE: 'FREE_TRAVEL_RAFFLE',
  FREE_1ST_SUBSCRIPTION_ENTRY: '1ST_SUBSCRIPTION_ENTRY_FREE',
  FREE_ENTRY_AND_EXTRA_TREE: 'FREE_ENTRY_AND_EXTRA_TREE',
};

interface IPaymentSection {
  onBack?: () => void;
}

const PaymentSection = observer(({ onBack }: IPaymentSection) => {
  const {
    entryMethod,
    orderId,
    adyenPaymentSession,
    haveValidAdyenPaymentSession,
    isValid,
    initialised,
    initialised_promocode,
    checked_user_referral,
    maybeContinueToPayment,
    checkOrderStatusAndAdvanceToConfirmation,
    orderError: errorFromEnterStore,
  } = useEnterStore();
  const { numberSetup } = useDrawStore();
  const { currentUser } = useUserStore();
  const { displayHeaderBanner } = useUIStore();
  const router = useRouter();

  const upgradePageSource = null;
  const [errorFromState, setError] = useState<string>();
  const error = errorFromState || errorFromEnterStore;

  if (entryMethod === EntryMethod.FreeSubscription)
    throw new Error("Free Subscriptions shouldn't use this component");

  const [loading, setLoading] = useState(true);
  const orderIdRef = useRef<string | null>(orderId);
  const loadingRef = useRef<boolean>(false);
  const isPollingRef = useRef<boolean>(false);
  const tenMinutes = 10 * 60 * 1000;

  const [
    showCorrectedPriceModalForNewAmount,
    setShowCorrectedPriceModalForNewAmount,
  ] = useState<number | undefined>();

  const utmParams = fetchUtmParamsJoinedWithFbDataFromLocalStorage();
  // Holds a reference to the timer that periodically checks for order completion.
  // Needed so we can clear it when the component unmounts.
  const orderCompletionTimerRef = useRef<any>(null);

  useEffect(() => {
    return () => {
      if (orderCompletionTimerRef.current) {
        clearTimeout(orderCompletionTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (
      initialised &&
      initialised_promocode &&
      checked_user_referral &&
      !haveValidAdyenPaymentSession &&
      numberSetup &&
      !loadingRef.current &&
      isValid &&
      currentUser
    ) {
      loadingRef.current = true;

      maybeContinueToPayment(/* ENG-394 this parameter used to be passed to handleContinueToPayment - orderIdRef*/)
        // eslint-disable-next-line promise/always-return
        .then(() => {
          loadingRef.current = false;
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error in handleContinueToPayment:', err);
          setError(
            'There was an issue setting up your payment. Please try again.'
          );
          setLoading(false);
          loadingRef.current = false;
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    haveValidAdyenPaymentSession,
    numberSetup,
    initialised,
    initialised_promocode,
    checked_user_referral,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    !loadingRef.current,
  ]);

  useEffect(() => {
    orderIdRef.current = orderId;
  }, [orderId]);

  useEffect(() => {
    if (haveValidAdyenPaymentSession && !isPollingRef.current) {
      isPollingRef.current = true;
      // Start periodically checking whether order is complete.
      clearTimeout(orderCompletionTimerRef.current);
      console.log('setup checkOrderStatusAndAdvanceToConfirmation');
      checkOrderStatusAndAdvanceToConfirmation(
        orderIdRef,
        orderCompletionTimerRef
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    haveValidAdyenPaymentSession,
    orderIdRef.current,
    orderCompletionTimerRef.current,
  ]);

  return (
    <div id="paymentSection">
      {!upgradePageSource && showCorrectedPriceModalForNewAmount && (
        <GenericModal
          onClose={() => setShowCorrectedPriceModalForNewAmount(undefined)}
        >
          <Subheading>Oops</Subheading>

          <p>
            The introductory offer is only available to new subscribers, so your
            order total is{' '}
            <strong>
              £
              {(showCorrectedPriceModalForNewAmount / 10000)
                .toFixed(2)
                .replace(/\.00/, '')}
            </strong>
          </p>

          <RoundedButton
            onClick={() => setShowCorrectedPriceModalForNewAmount(undefined)}
            href={undefined}
            className={undefined}
            id={undefined}
            disabled={undefined}
            hoverColor={undefined}
            hoverTextColor={undefined}
          >
            Got It
          </RoundedButton>
        </GenericModal>
      )}

      <div style={{ marginLeft: 'auto', marginRight: 'auto' }}>
        {error && error.length > 0 && (
          <div className="paddedMaxWidthContainer" style={{ margin: '0 auto' }}>
            <>
              <div style={{ height: '20px' }} />
              <Message
                id="errorMessage"
                negative
                style={{ width: '100%', maxWidth: '500px ' }}
              >
                {error}
              </Message>
            </>
          </div>
        )}

        {loading || !adyenPaymentSession ? (
          (
            // If there is no adyenPaymentSession, but there is an error, then we do not show the loading spinner. This
            // can happen if our backend detects the user is already a subscriber, so no adyenPayment session would have
            // been created.
            //
            // When there is an adyenPaymentSession, then the error appears alongside the AdyenDropInCheckout below
            error ? null : <PlainLoadingSpinner style={{ margin: '5em auto' }} />
          )
        ) : (
          <div
            className={`paddedMaxWidthContainer`}
            style={{ marginLeft: 'auto', marginRight: 'auto' }}
          >
            <AdyenDropInCheckout
              queryParamsObject={{
                source: upgradePageSource
                  ? SOURCE_QUERY_PARAM.UPGRADE
                  : SOURCE_QUERY_PARAM.STANDARD_TICKET_ORDER,
              }}
              paymentSession={adyenPaymentSession}
              onLoadingStateChange={(loading) => {
                setLoading(loading);
                scrollToSection('#__next');
              }}
              completionHandler={async () => {
                displayHeaderBanner(null);
                await trackEecCheckoutOption('Card');
              }}
              displayError={async (error) => {
                setError(error || undefined);
                if (!error) return;

                // Scroll to error
                await sleep(200); // Needed, otherwise form doesn't display (after cancelled payment), and to prevent `APP-X` sentry issue.
                scrollToSection('#errorMessage');
              }}
              style={{
                margin: '2em auto',
                width: '100%',
                maxWidth: 500,
              }}
              initialRedirectResult={null}
            />

            <div className="justify-between flex w-full">
              <img
                src={`${ASSET_PREFIX}/images/enter/payment-icons/secure-checkout.svg`}
              />
              <img
                src={`${ASSET_PREFIX}/images/enter/payment-icons/privacy-protected.svg`}
              />
              <img
                src={`${ASSET_PREFIX}/images/enter/payment-icons/trustpilot.svg`}
              />
            </div>

            {error && error.length > 0 && (
              <>
                <Message
                  id="errorMessage"
                  negative
                  style={{ width: '100%', maxWidth: '500px ' }}
                >
                  {error}
                </Message>
              </>
            )}
          </div>
        )}

        {!loading && onBack && (
          <BackButton
            onClick={() => {
              onBack();
            }}
            style={{
              margin: '5em auto 3em',
              display: 'block',
            }}
          />
        )}
      </div>

      <style jsx>{`
        #paymentSection {
          background: white;
          width: 100%;
        }

        :global(#paypal-button-container) {
          width: 100%;
        }
      `}</style>
    </div>
  );
});

export default PaymentSection;
